"use client";

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, subDays, startOfDay } from 'date-fns';

interface AIUsageChartsProps {
  timeRange: '24h' | '7d' | '30d' | 'all';
}

const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))'];

export function AIUsageCharts({ timeRange }: AIUsageChartsProps) {
  const { data: chartData, isLoading } = useQuery({
    queryKey: ['ai-usage-charts', timeRange],
    queryFn: async () => {
      let query = supabase
        .from('ai_conversation_logs')
        .select(`
          *,
          organizations(name),
          product_categories(name)
        `)
        .order('created_at', { ascending: true });
      
      // Apply time filter
      if (timeRange !== 'all') {
        const hours = timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : 720;
        const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
        query = query.gte('created_at', since);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      
      // Token usage over time (daily aggregates)
      const dailyUsage = data.reduce((acc, log) => {
        const date = format(startOfDay(new Date(log.created_at)), 'MMM dd');
        if (!acc[date]) {
          acc[date] = { date, tokens: 0, cost: 0 };
        }
        acc[date].tokens += (log.input_tokens || 0) + (log.output_tokens || 0);
        acc[date].cost += (log.estimated_cost_cents || 0) / 100;
        return acc;
      }, {} as Record<string, any>);
      
      // Cache efficiency over time
      const cacheEfficiency = data.reduce((acc, log) => {
        const date = format(startOfDay(new Date(log.created_at)), 'MMM dd');
        if (!acc[date]) {
          acc[date] = { date, totalInput: 0, cacheRead: 0 };
        }
        acc[date].totalInput += log.input_tokens || 0;
        acc[date].cacheRead += log.cache_read_tokens || 0;
        return acc;
      }, {} as Record<string, any>);
      
      const cacheEfficiencyData = Object.values(cacheEfficiency).map((item: any) => ({
        date: item.date,
        efficiency: item.totalInput > 0 ? (item.cacheRead / item.totalInput) * 100 : 0,
      }));
      
      // Cost by organization
      const costByOrg = data.reduce((acc, log) => {
        const org = log.organizations?.name || 'Unknown';
        if (!acc[org]) {
          acc[org] = { name: org, cost: 0 };
        }
        acc[org].cost += (log.estimated_cost_cents || 0) / 100;
        return acc;
      }, {} as Record<string, any>);
      
      // Usage by product category
      const usageByCategory = data.reduce((acc, log) => {
        const category = log.product_categories?.name || 'Unknown';
        if (!acc[category]) {
          acc[category] = { name: category, count: 0 };
        }
        acc[category].count += 1;
        return acc;
      }, {} as Record<string, any>);
      
      return {
        dailyUsage: Object.values(dailyUsage),
        cacheEfficiency: cacheEfficiencyData,
        costByOrg: Object.values(costByOrg).sort((a: any, b: any) => b.cost - a.cost).slice(0, 5),
        usageByCategory: Object.values(usageByCategory),
      };
    },
  });

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Loading charts...</div>;
  }

  const hasData = chartData && (
    (chartData.dailyUsage?.length > 0) ||
    (chartData.cacheEfficiency?.length > 0) ||
    (chartData.costByOrg?.length > 0) ||
    (chartData.usageByCategory?.length > 0)
  );

  if (!hasData) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p className="text-lg font-medium mb-2">No data to display</p>
        <p className="text-sm">Charts will populate as users interact with the AI Specification Assistant.</p>
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Token Usage Over Time</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData?.dailyUsage || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="tokens" stroke="hsl(var(--primary))" name="Tokens" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Cache Efficiency</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData?.cacheEfficiency || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="efficiency" stroke="hsl(var(--chart-2))" name="Cache Hit %" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Cost by Organization (Top 5)</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData?.costByOrg || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip formatter={(value: any) => `$${value.toFixed(2)}`} />
              <Bar dataKey="cost" fill="hsl(var(--chart-3))" name="Cost ($)" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
      
      <Card>
        <CardHeader>
          <CardTitle>Usage by Product Category</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData?.usageByCategory || []}
                dataKey="count"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label
              >
                {chartData?.usageByCategory?.map((entry: any, index: number) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
